// LandingPage.jsx
import React, { useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import ScrollButton from '../components/ScrollableButton/ScrollButton';
import AnimatedBookCallButton from '../components/BookCall/AnimatedBookCallButton.tsx';

export default function HeroSection() {
  useLayoutEffect(() => {
    let prevScrollRestoration;
    try {
      const params = new URLSearchParams(window.location.search);
      const fromLogoQuery = params.get('fromLogo');
      const fromLogoSession = sessionStorage.getItem('fromLogo');

      if (fromLogoQuery === '1' || fromLogoSession === '1') {
        // Prevent the browser from restoring the previous scroll position.
        try {
          if ('scrollRestoration' in window.history) {
            prevScrollRestoration = window.history.scrollRestoration;
            window.history.scrollRestoration = 'manual';
          }
        } catch (e) {
          // ignore — not supported
        }

        // Scroll to top. Use a tiny timeout so this runs after any browser layout.
        setTimeout(() => {
          try {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          } catch (e) {
            window.scrollTo(0, 0);
          }

          // Clear the session flag and remove the query param without reloading
          try { sessionStorage.removeItem('fromLogo'); } catch (err) { /* ignore */ }
          if (fromLogoQuery === '1') {
            params.delete('fromLogo');
            const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
            window.history.replaceState({}, '', newUrl);
          }
        }, 20);
      }
    } catch (err) {
      // ignore any errors
    }

    return () => {
      try {
        if (prevScrollRestoration !== undefined && 'scrollRestoration' in window.history) {
          window.history.scrollRestoration = prevScrollRestoration;
        }
      } catch (e) {
        // ignore
      }
    };
  }, []);
  return (
    <section
      id="hero"  
      className="relative flex items-center justify-center"
      style={{
        minHeight: "calc(100vh - 6rem)", // full screen minus header height
      }}
    >
      {/* ✅ Mobile Background Video */}
      <div className="lg:hidden absolute inset-0 -z-10">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source
            src="/background/videos/FinalHeroVideo.mp4"
            type="video/mp4"
          />
        </video>
        {/* overlay to keep text readable but video visible */}
        <div className="absolute inset-0 bg-white/60"></div>
      </div>

      {/* ✅ Gradient background only for large screens */}
      <div className="hidden lg:block absolute inset-0 -z-20"
        style={{
          background: "linear-gradient(to right bottom, rgb(244, 250, 250) 12%, rgb(216 232 254) 85%)",
        }}
      ></div>

      {/* Wrapper grid */}
      <div className="w-full grid lg:grid-cols-2 items-center relative z-10">
        
        {/* Left Side - CTA Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-left px-6 pl-6 ml-16 sm:px-10 lg:px-16 land-left"
        >
          {/* Logo */}
          <motion.img
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            src="/images/RLogo.png"
            alt="Logo"
            className="w-32 h-32 mb-2"
          />

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-4xl lg:text-7xl font-bold text-gray-900 mb-4 leading-tight"
            style={{ fontFamily: "'Bright Demo', sans-serif" }}
          >
            BYTEON
          </motion.h1>

          {/* Subheading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-lg lg:text-2xl text-accent font-medium mb-6"
          >
            Byte into the Future
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="text-base lg:text-xl text-gray-700 mb-8 leading-relaxed max-w-lg"
          >
            Innovating software solutions that drive tomorrow's advancements, powered by cutting-edge AI technologies.
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
             <AnimatedBookCallButton variant="inline" />
          </motion.div>
        </motion.div>

        {/* ✅ Right Side Video for desktop */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative hidden lg:block w-full h-full"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover border-none"
          >
            <source
              src="/background/videos/FinalHeroVideo.mp4"
              type="video/mp4"
            />
          </video>
        </motion.div>
      </div>

  <ScrollButton />
    </section>
  );
}
